import { useEffect, useState } from 'react';

export type AnimationState = 'entering' | 'visible' | 'exiting';

interface UseAnimationStateOptions {
  /** Delay before entering state becomes visible (ms) */
  enterDelay?: number;
  /** Delay before exiting state removes from DOM (ms) */
  exitDelay?: number;
  /** Callback when animation completes exit */
  onExitComplete?: () => void;
}

interface UseAnimationStateResult {
  /** Whether the component should be rendered in the DOM */
  shouldRender: boolean;
  /** Current animation state for CSS class application */
  animationState: AnimationState;
}

/**
 * Hook for three-state animation pattern.
 * Handles the common pattern of enter/visible/exit states for animated overlays.
 *
 * @param isVisible - Whether the component should be visible
 * @param options - Animation timing options
 */
export function useAnimationState(
  isVisible: boolean,
  options: UseAnimationStateOptions = {},
): UseAnimationStateResult {
  const { enterDelay = 300, exitDelay = 300, onExitComplete } = options;

  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<AnimationState>('exiting');

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), enterDelay);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => {
        setShouldRender(false);
        onExitComplete?.();
      }, exitDelay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, enterDelay, exitDelay, onExitComplete]);

  return { shouldRender, animationState };
}
